// @flow

import * as React from 'react';

import type { ProductError } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectIsPostingCheckout } from 'src/modules/app/selectors';

import ErrorItem from './ErrorItem';

type Props = {
  handleRemoveAction: (SyntheticEvent<HTMLButtonElement>) => mixed,
  errors?: Array<ProductError>,
};

function RemoveButton({
  handleRemoveAction,
}: {
  handleRemoveAction: Props['handleRemoveAction'],
}) {
  const { __ } = useTranslator();

  return (
    <Button kind="link" type="button" onClick={handleRemoveAction}>
      {__('Remove product')}
    </Button>
  );
}

export default function ErrorRow({
  handleRemoveAction,
  errors,
}: Props): null | React.MixedElement {
  const { __ } = useTranslator();

  const isPostingCheckout = useAppSelector(selectIsPostingCheckout);

  const messageItems = React.useMemo(() => {
    if (!errors || errors.length === 0)
      return (
        <ErrorItem
          key="errors"
          cta={<RemoveButton handleRemoveAction={handleRemoveAction} />}
        >
          {__(
            'Sorry, something unexpected happened. Retry later or remove this product to continue.',
          )}
        </ErrorItem>
      );

    return (
      <ErrorItem
        cta={<RemoveButton handleRemoveAction={handleRemoveAction} />}
        options={errors.map(err => err.description)}
        title={__('Error:')}
      />
    );
  }, [__, errors, handleRemoveAction]);

  if (isPostingCheckout) return null;
  return (
    <Base flex flexCol gap="spacing-1x">
      {messageItems}
    </Base>
  );
}
